import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Home } from "lucide-react";
import { Btn } from "@/components/Btn";
import { CTA } from "@/lib/brand";
import { TOP_SERVICES, servicePath } from "@/lib/services";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services", hasMenu: true },
  { label: "Service Areas", to: "/service-areas" },
  { label: "About", to: "/about" },
  { label: "Gallery", to: "/gallery" },
  { label: "FAQ", to: "/faq" },
  { label: "Give Back", to: "/give-back" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [svc, setSvc] = useState(false);
  const loc = useLocation();
  useEffect(() => { setOpen(false); setSvc(false); }, [loc.pathname]);

  return (
    <header className="sticky top-0 z-50 bg-white border-b-2 border-charcoal">
      <div className="mx-auto max-w-7xl px-4 md:px-8 h-16 md:h-20 grid grid-cols-2 md:grid-cols-[1fr_auto_1fr] items-center gap-4">
        <Link to="/" data-testid="header-logo-link" className="font-heading font-bold text-lg md:text-2xl text-charcoal leading-none justify-self-start">
          Skedaddle<span className="text-coral"> Junk Removal</span>
        </Link>

        {/* Desktop centered nav */}
        <nav className="hidden md:flex items-center justify-center gap-7" data-testid="desktop-nav">
          {NAV.map((n) => n.hasMenu ? (
            <div key={n.to} className="relative" onMouseEnter={() => setSvc(true)} onMouseLeave={() => setSvc(false)}>
              <Link to={n.to} className="font-heading font-bold text-[17px] hover:text-coral transition-colors inline-flex items-center gap-1">
                {n.label} <ChevronDown className="w-4 h-4" />
              </Link>
              {svc && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3">
                  <div className="w-64 bg-white border-2 border-charcoal rounded-2xl shadow-brut p-2 grid" data-testid="services-dropdown">
                    {TOP_SERVICES.map((s) => (
                      <Link key={s.slug} to={servicePath(s)} className="px-3 py-2 rounded-xl font-body font-semibold hover:bg-sunny transition-colors">{s.title}</Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Link key={n.to} to={n.to} className="font-heading font-bold text-[17px] hover:text-coral transition-colors">{n.label}</Link>
          ))}
        </nav>

        <div className="hidden md:flex justify-self-end">
          <Btn to="/contact" size="md" data-testid="nav-quote-btn">{CTA.quote}</Btn>
        </div>

        {/* Mobile controls */}
        <div className="flex md:hidden items-center gap-2 justify-self-end">
          <Link to="/" data-testid="mobile-home-btn" aria-label="Home"
            className="inline-flex items-center justify-center w-11 h-11 rounded-full border-2 border-charcoal bg-white shadow-brutsm"><Home className="w-5 h-5" /></Link>
          <button onClick={() => setOpen((o) => !o)} data-testid="hamburger-btn" aria-label="Menu"
            className="inline-flex items-center justify-center w-11 h-11 rounded-full border-2 border-charcoal bg-sunny shadow-brutsm">{open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}</button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {open && (
        <div className="md:hidden border-t-2 border-charcoal bg-white px-4 py-4 space-y-1" data-testid="mobile-menu">
          <Link to="/" className="block px-3 py-3 rounded-xl font-heading font-bold hover:bg-sunny">Home</Link>
          <button onClick={() => setSvc((s) => !s)} className="w-full flex items-center justify-between px-3 py-3 rounded-xl font-heading font-bold hover:bg-sunny" data-testid="mobile-services-toggle">
            Services <ChevronDown className={`w-5 h-5 transition-transform ${svc ? "rotate-180" : ""}`} />
          </button>
          {svc && (
            <div className="pl-4 space-y-1">
              <Link to="/services" className="block px-3 py-2 rounded-xl font-body font-semibold hover:bg-cream">All Services</Link>
              {TOP_SERVICES.map((s) => (
                <Link key={s.slug} to={servicePath(s)} className="block px-3 py-2 rounded-xl font-body font-medium hover:bg-cream">{s.title}</Link>
              ))}
            </div>
          )}
          {NAV.slice(2).map((n) => (
            <Link key={n.to} to={n.to} className="block px-3 py-3 rounded-xl font-heading font-bold hover:bg-sunny">{n.label}</Link>
          ))}
          <Btn to="/contact" size="md" className="w-full mt-2" data-testid="mobile-quote-btn">{CTA.quote}</Btn>
        </div>
      )}
    </header>
  );
}
